import React from 'react'
import styled, { css } from 'styled-components'

import isNaN from 'lodash/isNaN'
import clamp from 'ramda/src/clamp'

import { THB_SHORT, THB_FULL } from '../../constants'
import { percentage, isSameSign } from '../../lib/utils'

import Tooltip, { withTooltip } from '../../lib/components/Tooltip'

const fallbackNaN = (number) => (isNaN(number) ? '0.00' : number)

/**
 * @typedef {object} Props
 * @property {number} current_price
 * @property {number} approved_plan
 * @property {number} forecast
 * @property {number} actual
 * @property {number} remaining
 */

const percentRange = clamp(-100, 100)
const z_index = [7621, 7622, 7623]

/** @param {Props} props */
function IndicatorGP(props) {
  const { current_price = 1, approved_plan = 0, forecast = 0, actual = 0, remaining = 0 } = props

  // handling 0/0 case
  const current_price_no_zero = current_price || 1

  const percent = {
    approved_plan: percentage(approved_plan, current_price_no_zero),
    forecast: percentage(forecast, current_price_no_zero),
    actual: percentage(actual, current_price_no_zero),
    remaining: percentage(
      isSameSign(actual, remaining) ? forecast : remaining,
      current_price_no_zero
    ),
  }
  const color = {
    actual: percent.actual >= 0 ? '#5cb85c' : '#d44b49',
    remaining: percent.remaining >= 0 ? '#9be89b' : '#ecadad',
  }

  return (
    <Bar>
      <Marker />
      <Marker style={ { borderRightStyle: 'dashed' } } percent={ percentRange(fallbackNaN(percent.forecast)) } />
      <Marker expand percent={ percentRange(fallbackNaN(percent.approved_plan)) }>
        <Label name="Approved Plan GP">{THB_SHORT(fallbackNaN(approved_plan))}</Label>
      </Marker>
      <Data color={ color.remaining } percent={ percentRange(fallbackNaN(percent.remaining)) } />
      <Data color={ color.actual } percent={ percentRange(fallbackNaN(percent.actual)) } />
      <Tooltip data-testid="tooltip">
        <Detail color={ color.actual }>
          <div>Actual GP:</div>
          <Number>{THB_FULL(fallbackNaN(actual))}</Number>
        </Detail>
        <Detail color={ color.remaining }>
          <div>Remaining GP:</div>
          <Number>{THB_FULL(fallbackNaN(remaining))}</Number>
        </Detail>
        <Detail color="#404040">
          <div>Forecast GP:</div>
          <Number>{THB_FULL(fallbackNaN(forecast))}</Number>
        </Detail>
        <Detail color="#707070">
          <div>Current Price:</div>
          <Number>{THB_FULL(fallbackNaN(current_price))}</Number>
        </Detail>
      </Tooltip>
    </Bar>
  )
}

// prettier-ignore
const modifiers = {
  marker: {
    expand: ({ expand }) => expand && css`
      height: 50px;
      color: #404040;

      border-right-style: solid;
      border-right-color: currentColor;

      &::before {
        content: '';
        width: 6px;
        height: 6px;

        position: absolute;
        top: 0;
        left: 0;
        transform: translate(-42%, -42%);

        border-radius: 50%;
        background-color: currentColor;
      }
    `,
    percent: ({ percent = 0 }) => css`
      left: calc((100% - 8px) * ${(percent / 200) + 0.5} + 4px);
    `,
  },
  label: {
    name: ({ name = '' }) => name,
  },
  data: {
    percent: ({ percent = 0 }) => css`
      width: calc(${Math.abs(percent) / 2}% - ${percent === 100 ? 3 : percent === -100 ? 4 : 2}px);
      ${percent > 0 ? 'left' : 'right'}: 50%;
      border-top-${percent > 0 ? 'left' : 'right'}-radius: 0;
      border-bottom-${percent > 0 ? 'left' : 'right'}-radius: 0;
    `,
    color: ({ color = '#B2DDF7' }) => color,
  },
}

export const Bar = styled.div`
  width: 570px;
  height: 14px;
  padding: 0 4px;

  position: relative;
  /* z-index: ${z_index[0]}; */

  display: flex;
  align-items: center;

  border-radius: 8px;
  border: 1px solid #ccc;
  background-color: #f9f9f9;

  cursor: pointer;

  ${withTooltip}
`

const Marker = styled.div`
  height: 25px;

  position: absolute;
  z-index: ${z_index[2]};
  bottom: -5px;

  border-right: thin dotted #707070;

  ${modifiers.marker.expand}
  ${modifiers.marker.percent}
`

const Label = styled.div`
  width: max-content;

  position: absolute;
  right: 5px;
  text-align: right;

  color: #333;
  font-family: 'Gotham-Medium';
  font-size: 12px;
  line-height: 12px;

  &::before {
    content: '${modifiers.label.name}';
    display: block;
    margin-bottom: 2px;

    color: #a1a1a1;
    font-family: 'Gotham';
    font-size: 10px;
    line-height: 10px;
  }
`

const Data = styled.div`
  height: 6px;

  position: absolute;
  z-index: ${z_index[1]};

  border-radius: 6px;
  background-color: ${modifiers.data.color};

  cursor: pointer;

  ${modifiers.data.percent}
`

const Detail = styled.div`
  padding-top: 1px;

  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;

  color: #333333;
  font-family: 'Gotham';
  font-size: 12px;
  line-height: 15px;

  &:not(:last-child) {
    margin-bottom: 6px;
  }

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    margin-right: 6px;

    background-color: ${modifiers.data.color};
    border-radius: 50%;
  }

  > :first-child {
    margin-right: 8px;
  }
`

const Number = styled.span`
  font-family: 'NotoSans';
`

export default IndicatorGP



// WEBPACK FOOTER //
// ./src/components/project-profit-sheet/IndicatorGP.jsx